@if (visible()) {
  <aside class="history-viewer"
         [attr.data-consumes-wheel]="''"
         [style.width.px]="customWidth()"
         [style.max-width]="customWidth() !== null ? 'none' : null"
         [attr.aria-label]="'history.viewer-title' | t">
    <div class="history-viewer-resizer"
         [attr.aria-label]="'history.resize' | t"
         (pointerdown)="startResize($event)"></div>
    <header class="history-viewer-header">
      <span class="title">{{ 'history.viewer-title' | t }}</span>
      <span class="count">{{ total() }}</span>
      @if (selectedCount() > 0) {
        <span class="selection-gap"></span>
        <span class="selection-count">{{ selectedCount() }}</span>
        @if (selectedCount() === 1) {
          <button type="button"
                  class="selection-action save-as-head"
                  [attr.aria-label]="'history.selection-make-head' | t"
                  [title]="'history.selection-make-head' | t"
                  (click)="makeHeadSelection()">
            {{ 'history.save-as' | t }}
          </button>
        } @else {
          <button type="button"
                  class="selection-action merge-to-head"
                  [attr.aria-label]="'history.selection-merge' | t"
                  [title]="'history.selection-merge' | t"
                  (click)="openMergePreview()">
            {{ 'history.selection-merge' | t }}
          </button>
        }
      }
      <button type="button"
              class="group-step-toggle"
              [class.active]="groupStepEnabled()"
              [attr.aria-pressed]="groupStepEnabled()"
              [attr.aria-label]="'history.group-step' | t"
              [title]="'history.group-step' | t"
              (click)="toggleGroupStep()">⁘</button>
      <button type="button"
              class="close"
              [attr.aria-label]="'history.hide' | t"
              [title]="'history.hide' | t"
              (click)="hide()">×</button>
    </header>
    @if (filterCategories().length > 0) {
      <div class="filter-bar" role="toolbar" (wheel)="$event.stopPropagation()">
        @for (def of filterCategories(); track def.id) {
          <button type="button"
                  class="filter-toggle"
                  [class.active]="isCategoryEnabled(def.id)"
                  [attr.data-category]="def.id"
                  [attr.aria-pressed]="isCategoryEnabled(def.id)"
                  [attr.aria-label]="def.id"
                  [title]="def.id"
                  (click)="toggleCategory(def.id)">
            <hc-icon [ref]="def.icon ?? null"></hc-icon>
          </button>
        }
      </div>
    }
    <ul class="rows" (wheel)="$event.stopPropagation()">
      <li class="row anchor anchor-head" aria-hidden="true">
        <span class="indicator"></span>
        <span class="idx">—</span>
        <span class="summary">{{ 'history.anchor-head' | t }}</span>
      </li>
      @for (row of rows(); track row.index) {
        <li class="row"
            [class.active]="row.active"
            [class.selected]="isRowSelected(row.filename)"
            [attr.data-category]="row.category"
            (click)="onRowClick(row, $event)"
            (dblclick)="openSlice(row.index, $event)">
          @if (categoryDef(row.category).icon; as icon) {
            <hc-icon class="row-icon" [ref]="icon"></hc-icon>
          } @else {
            <span class="indicator"></span>
          }
          <span class="idx">{{ row.label }}</span>
          <span class="summary">{{ row.summary }}</span>
          <button type="button"
                  class="row-action row-make-head"
                  [attr.aria-label]="'history.make-head' | t"
                  [title]="'history.make-head' | t"
                  (click)="promoteRow(row.index, $event)">^</button>
          <span class="when">{{ row.when }}</span>
          <button type="button"
                  class="row-action row-delete"
                  [attr.aria-label]="'history.delete-entry' | t"
                  [title]="'history.delete-entry' | t"
                  (click)="deleteRow(row.index, $event)">×</button>
        </li>
      }
      <li class="row anchor anchor-start"
          [class.active]="position() === 0"
          (click)="seek(-1)">
        <span class="indicator"></span>
        <span class="idx">—</span>
        <span class="summary">{{ 'history.anchor-start' | t }}</span>
      </li>
    </ul>
  </aside>

  @if (sliceCurrent(); as slice) {
    <div class="slice-backdrop" (click)="closeSlice()"></div>
    <div class="slice-modal" role="dialog" aria-modal="true">
      <header>
        @if (canSliceBack()) {
          <button type="button"
                  class="slice-back"
                  [attr.aria-label]="'history.back' | t"
                  [title]="'history.back' | t"
                  (click)="sliceBack()">←</button>
        }
        <span class="slice-label">{{ slice.label }}</span>
        <button type="button"
                class="slice-copy"
                [class.copied]="sliceCopied()"
                [attr.aria-label]="'history.copy-slice' | t"
                (click)="copySliceJson()">{{ sliceCopied() ? '✓' : 'copy' }}</button>
        <button type="button"
                class="slice-close"
                [attr.aria-label]="'history.close-slice' | t"
                (click)="closeSlice()">×</button>
      </header>
      <pre class="slice-json">@for (line of slice.lines; track $index) {@if (lineSig(line.text); as sig) {<span class="slice-line slice-sig" [attr.data-status]="line.status" [attr.data-sig]="sig" (click)="drillIntoSig(sig)" role="button" tabindex="0">{{ line.text }}</span>
} @else {<span class="slice-line" [attr.data-status]="line.status">{{ line.text }}</span>
}}</pre>
      @if (slice.properties; as props) {
        <div class="slice-section">
          <div class="slice-section-header">0000 (tile properties)</div>
          <pre class="slice-json slice-props">{{ props }}</pre>
        </div>
      }
    </div>
  }

  @if (mergePreview(); as preview) {
    <div class="slice-backdrop" (click)="closeMergePreview()"></div>
    <div class="slice-modal merge-preview-modal" role="dialog" aria-modal="true">
      <header>
        <span class="slice-label">{{ 'history.merge-preview-title' | t }} · {{ preview.sourceCount }}</span>
        <button type="button"
                class="merge-commit"
                [disabled]="mergeCommitting()"
                [attr.aria-label]="'history.merge-preview-commit' | t"
                (click)="commitMergePreview()">{{ 'history.merge-preview-commit' | t }}</button>
        <button type="button"
                class="slice-close"
                [attr.aria-label]="'history.merge-preview-cancel' | t"
                (click)="closeMergePreview()">×</button>
      </header>
      <pre class="slice-json">@for (line of preview.lines; track $index) {@if (lineSig(line.text); as sig) {<span class="slice-line slice-sig" [attr.data-status]="line.status" [attr.data-sig]="sig" (click)="drillIntoSig(sig)" role="button" tabindex="0">{{ line.text }}</span>
} @else {<span class="slice-line" [attr.data-status]="line.status">{{ line.text }}</span>
}}</pre>
    </div>
  }
}
